import { useState } from "react";
import { useContextoApp } from "@/app/contexto";
import { useApiResource } from "@/hooks/useApiResource";
import { useCartelInstalar } from "@/hooks/useCartelInstalar";
import type { BadgeTono } from "@/components";
import type { Avance, FiduciantePublico, ListadoCuentas, GrupoPaso, ListadoLlamados, ListadoNovedades, Presupuesto, ResumenUnidades, ResumenVentas, SaldosCaja } from "@/lib/api";
import { hayPrevision, tarjetaPrevision } from "@/lib/cuentas";
import { hayAvance, tarjetaAvance } from "@/lib/avance";
import { haySaldo, tarjetaCaja } from "@/lib/caja";
import { hayPresupuesto, tarjetaPresupuesto } from "@/lib/presupuesto";
import { PASOS, abiertoInicial, agruparPasos, guardarPlegado, leerPlegado, tituloPuestaEnMarcha } from "@/lib/puestaEnMarcha";
import { hayUnidades, tarjetaUnidades } from "@/lib/unidades";
import { rolesDe } from "@/lib/navegacion";
import { hayVentas, tarjetaVentas } from "@/lib/ventas";

export interface PasoInicio {
  clave: string;
  titulo: string;
  ruta: string;
  completo: boolean;
  detalle: string | null;
}

export interface GrupoInicio {
  clave: GrupoPaso;
  titulo: string;
  descripcion: string;
  completos: number;
  total: number;
  completo: boolean;
  enEspera: boolean;
  pasos: PasoInicio[];
}

export interface PuestaEnMarchaInicio {
  titulo: string;
  completa: boolean;
  grupos: GrupoInicio[];
  abierto: boolean;
  /** El admin lo abrió o plegó a mano: se recuerda en el navegador. */
  onCambio: (abierto: boolean) => void;
  /** Mientras falte algo: qué menú se ve y el link para cambiarlo (también está al pie de la barra en la compu). */
}

export interface TarjetaInicio {
  titulo: string;
  valor: string;
  detalle?: string;
}

/**
 * Lógica de Inicio de admin, contador y escribano (sin JSX; el fiduciante tiene el suyo, `useNovedadesTab`).
 * El escribano no ve fiduciantes: no se pide nada. Todos los roles piden los resúmenes
 * de unidades, de ventas y del avance de obra. Caja y presupuesto los leen admin y contador; al escribano
 * ni se le pide. Las tarjetas de números aparecen recién cuando tienen algo (`hay*`): un desarrollo recién creado no
 * muestra ceros. Si una falla, sí aparece con el aviso.
 */
export function useInicioTab() {
  const { usuario, membresia, fid, puestaEnMarcha: puesta } = useContextoApp();
  const rol = membresia.rol;
  const cartel = useCartelInstalar(rol);
  const fiduciantes = useApiResource<FiduciantePublico[]>(rol === "escribano" ? null : `/api/fideicomisos/${fid}/fiduciantes`);
  const lista = fiduciantes.data;

  let tarjeta: TarjetaInicio | null = null;
  if (rol === "admin" || rol === "contador") {
    if (fiduciantes.error) tarjeta = { titulo: "Fiduciantes", valor: "—", detalle: "No se pudo cargar" };
    else if (lista && lista.length > 0) tarjeta = { titulo: "Fiduciantes", valor: String(lista.length) };
  }

  const llamados = useApiResource<ListadoLlamados>(rol === "admin" || rol === "contador" ? `/api/fideicomisos/${fid}/llamados` : null);

  let tarjetaCuenta: (TarjetaInicio & { link?: string; textoLink?: string }) | null = null;
  if (rol === "admin" || rol === "contador") {
    if (llamados.data && llamados.data.items.length > 0) {
      const abiertos = llamados.data.items.filter((l) => l.estado === "abierto").length;
      const vencidos = llamados.data.items.filter((l) => l.estado === "vencido").length;
      tarjetaCuenta = { titulo: "Previsiones de gastos", valor: String(llamados.data.items.length), detalle: `${abiertos} abierta${abiertos === 1 ? "" : "s"} · ${vencidos} vencida${vencidos === 1 ? "" : "s"}`, link: "/llamados", textoLink: "Ver previsiones" };
    } else if (llamados.error) {
      tarjetaCuenta = { titulo: "Previsiones de gastos", valor: "—", detalle: "No se pudo cargar" };
    }
  }

  // Previsión: la plata adelantada que queda (cuentas corrientes). Sólo el admin.
  const cuentas = useApiResource<ListadoCuentas>(rol === "admin" ? `/api/fideicomisos/${fid}/cuentas` : null);
  let tarjetaPrevisionInicio: (TarjetaInicio & { tono?: BadgeTono; link?: string; textoLink?: string }) | null = null;
  if (cuentas.data && hayPrevision(cuentas.data.totales)) {
    tarjetaPrevisionInicio = { titulo: "Previsión: queda", ...tarjetaPrevision(cuentas.data.totales), link: "/cuentas-corrientes", textoLink: "Ver cuentas corrientes" };
  } else if (cuentas.error) {
    tarjetaPrevisionInicio = { titulo: "Previsión: queda", valor: "—", detalle: "No se pudo cargar" };
  }

  const unidades = useApiResource<ResumenUnidades>(`/api/fideicomisos/${fid}/unidades/resumen`);
  let tarjetaUnidadesInicio: (TarjetaInicio & { link?: string; textoLink?: string }) | null = null;
  if (unidades.data && hayUnidades(unidades.data)) {
    tarjetaUnidadesInicio = { titulo: "Unidades", ...tarjetaUnidades(unidades.data), link: "/unidades", textoLink: "Ver unidades" };
  } else if (unidades.error) {
    tarjetaUnidadesInicio = { titulo: "Unidades", valor: "—", detalle: "No se pudo cargar" };
  }

  const ventas = useApiResource<ResumenVentas>(`/api/fideicomisos/${fid}/ventas/resumen`);
  let tarjetaVentasInicio: (TarjetaInicio & { link?: string; textoLink?: string }) | null = null;
  if (ventas.data && hayVentas(ventas.data)) tarjetaVentasInicio = { titulo: "Ventas", ...tarjetaVentas(ventas.data), link: "/ventas", textoLink: "Ver ventas" };
  else if (ventas.error) tarjetaVentasInicio = { titulo: "Ventas", valor: "—", detalle: "No se pudo cargar" };

  const saldos = useApiResource<SaldosCaja>(rol === "escribano" ? null : `/api/fideicomisos/${fid}/caja/saldos`);
  let tarjetaCajaInicio: (TarjetaInicio & { link?: string; textoLink?: string }) | null = null;
  if (saldos.data && haySaldo(saldos.data)) tarjetaCajaInicio = { titulo: "Caja", ...tarjetaCaja(saldos.data), link: "/caja", textoLink: "Ver caja" };
  else if (saldos.error) tarjetaCajaInicio = { titulo: "Caja", valor: "—", detalle: "No se pudo cargar" };

  const presupuesto = useApiResource<Presupuesto>(rol === "escribano" ? null : `/api/fideicomisos/${fid}/presupuesto`);
  let tarjetaPresupuestoInicio: (TarjetaInicio & { link?: string; textoLink?: string }) | null = null;
  if (presupuesto.data && hayPresupuesto(presupuesto.data)) tarjetaPresupuestoInicio = { titulo: "Presupuesto", ...tarjetaPresupuesto(presupuesto.data), link: "/presupuesto", textoLink: "Ver presupuesto" };
  else if (presupuesto.error) tarjetaPresupuestoInicio = { titulo: "Presupuesto", valor: "—", detalle: "No se pudo cargar" };

  const avance = useApiResource<Avance>(`/api/fideicomisos/${fid}/avance`);
  let tarjetaAvanceInicio: (TarjetaInicio & { link?: string; textoLink?: string; porcentaje?: number | null }) | null = null;
  if (avance.data && hayAvance(avance.data)) tarjetaAvanceInicio = { titulo: "Avance de obra", ...tarjetaAvance(avance.data), link: "/avance", textoLink: "Ver avance" };
  else if (avance.error) tarjetaAvanceInicio = { titulo: "Avance de obra", valor: "—", detalle: "No se pudo cargar" };

  const novedades = useApiResource<ListadoNovedades>(`/api/fideicomisos/${fid}/avance/novedades?limite=3`);

  // Puesta en marcha: sólo el admin. La pide Privado (`useMenu`) y llega por el contexto.
  // Abierto mientras falte algo salvo que lo haya plegado a mano (localStorage).
  const [plegadoManual, setPlegadoManual] = useState<boolean | null>(null);
  let puestaEnMarcha: PuestaEnMarchaInicio | null = null;
  if (puesta.data) {
    const estado = puesta.data;
    puestaEnMarcha = {
      titulo: tituloPuestaEnMarcha(estado),
      completa: estado.completa,
      grupos: agruparPasos(estado).map((g) => ({
        ...g,
        pasos: g.pasos.map((p) => ({ clave: p.clave, ...PASOS[p.clave], completo: p.completo, detalle: p.detalle })),
      })),
      abierto: plegadoManual ?? abiertoInicial(estado, leerPlegado(fid)),
      onCambio: (abierto) => {
        setPlegadoManual(abierto);
        guardarPlegado(fid, abierto);
      },
    };
  }

  const ultimas = novedades.data?.items.length ? novedades.data.items : null;
  const tarjetas = [tarjetaPrevisionInicio, tarjeta, tarjetaCuenta, tarjetaUnidadesInicio, tarjetaVentasInicio, tarjetaCajaInicio, tarjetaPresupuestoInicio, tarjetaAvanceInicio];
  const cargando = [fiduciantes, llamados, cuentas, unidades, ventas, saldos, presupuesto, avance, novedades].some((r) => r.loading);
  // Sin checklist (no es admin) y sin nada que mostrar: que no quede la pantalla vacía.
  const sinDatos = rol !== "admin" && !cargando && !ultimas && tarjetas.every((x) => x === null);

  // Terreno no está en el menú (Mati, 2026-11-03: es donde empieza todo): mientras falte la puesta en marcha se llega
  // desde su tarjeta; después, desde este link (y desde Avance).
  const verTerreno = rolesDe("/terreno").includes(rol) && !(puestaEnMarcha && !puestaEnMarcha.completa);

  return {
    verTerreno,
    nombre: usuario.nombre.split(" ")[0] ?? usuario.nombre,
    rol,
    cartelInstalar: cartel.visible,
    cerrarCartel: cartel.cerrar,
    tarjetaPrevision: tarjetaPrevisionInicio,
    tarjeta,
    tarjetaCuenta,
    tarjetaUnidades: tarjetaUnidadesInicio,
    tarjetaVentas: tarjetaVentasInicio,
    tarjetaCaja: tarjetaCajaInicio,
    tarjetaPresupuesto: tarjetaPresupuestoInicio,
    tarjetaAvance: tarjetaAvanceInicio,
    puestaEnMarcha,
    puestaEnMarchaError: puesta.error && !puesta.data ? "No se pudo cargar la puesta en marcha" : null,
    novedades: ultimas,
    sinDatos,
  };
}
